import {
  ArrowRight,
  Check,
  CreditCard,
  Crown,
  Shield,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface PlanDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  plan: any;
  currentPlan: any;
  allPlans?: any[]; // برای مقایسه check_count
  onUpgrade: (plan: any) => void;
}

const PlanDetailsDialog: React.FC<PlanDetailsDialogProps> = ({
  isOpen,
  onClose,
  plan,
  currentPlan,
  allPlans = [],
  onUpgrade,
}) => {
  const [loading, setLoading] = useState(false);

  if (!plan) return null;

  // Format prices coming in minor units (e.g., cents) like 4900 -> "49"
  const formatPrice = (amountMinor: number) => {
    if (amountMinor == null) return "-";
    const major = amountMinor / 100;
    const asString = major.toFixed(2);
    return asString.replace(/\.00$/, "");
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return <Zap className="h-8 w-8 text-blue-500" />;
      case "premium":
        return <Star className="h-8 w-8 text-purple-500" />;
      case "enterprise":
        return <Crown className="h-8 w-8 text-emerald-500" />;
      default:
        return <CreditCard className="h-8 w-8 text-gray-500" />;
    }
  };

  const getPlanBenefits = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return [
          "Fast and accurate identity verification",
          "Email support",
          "Basic reporting",
          "High security standards",
          "Standard processing time",
        ];
      case "premium":
        return [
          "All Basic plan features",
          "Phone support",
          "Advanced reporting",
          "Priority processing",
          "API access",
          "Custom integrations",
        ];
      case "enterprise":
        return [
          "All Premium plan features",
          "Dedicated support",
          "Custom reporting",
          "Integration with existing systems",
          "Multi-organization management",
          "White-label solutions",
          "SLA guarantees",
        ];
      default:
        return ["Standard features"];
    }
  };

  const getPlanGradient = (planName: string) => {
    switch (planName.toLowerCase()) {
      case "basic":
        return "from-blue-500 to-blue-600";
      case "premium":
        return "from-purple-500 to-purple-600";
      case "enterprise":
        return "from-emerald-500 to-emerald-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  // تابع برای بررسی اینکه آیا plan جدید upgrade است
  const isPlanUpgrade = (): boolean => {
    if (
      !currentPlan ||
      !currentPlan.planName ||
      currentPlan.planName === "free"
    ) {
      return true; // اگر plan فعلی free است، همه plan‌ها upgrade هستند
    }

    // پیدا کردن plan فعلی در لیست allPlans
    const currentPlanData = allPlans.find((p) => p.id === currentPlan.planName);
    const currentCheckCount =
      currentPlanData?.checkCount ||
      parseInt(currentPlanData?.productMetadata?.check_count || "0") ||
      0;

    // دریافت check_count plan جدید
    const newCheckCount =
      plan.checkCount ||
      parseInt(plan.productMetadata?.check_count || "0") ||
      0;

    // اگر check_count plan جدید بیشتر باشد، upgrade است
    return newCheckCount > currentCheckCount;
  };

  const isCurrentPlan = currentPlan?.planName === plan.id;
  const isUpgrade = isPlanUpgrade();
  const isDowngrade =
    !isCurrentPlan &&
    !isUpgrade &&
    currentPlan?.planName &&
    currentPlan.planName !== "free";

  const handleUpgrade = async () => {
    if (isDowngrade) {
      return; // جلوگیری از downgrade
    }
    try {
      setLoading(true);
      await onUpgrade(plan);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-background border-border">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-full bg-gradient-to-r from-primary/10 to-primary/5">
              {getPlanIcon(plan.name)}
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold">
                {plan.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {plan.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Price Section */}
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-6 text-center">
            <div className="flex items-baseline justify-center gap-1 mb-2">
              <span className="text-5xl font-bold text-primary">
                ${formatPrice(plan.pricePerMonth)}
              </span>
              <span className="text-muted-foreground">/month</span>
            </div>
            <p className="text-muted-foreground">
              {plan.currency.toUpperCase()} • {plan.interval} billing
            </p>
            {plan.isPopular && (
              <Badge className="mt-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                <Sparkles className="h-3 w-3 mr-1" />
                Most Popular
              </Badge>
            )}
          </div>

          {/* Features Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              Plan Features
            </h3>
            <div className="grid grid-cols-1 gap-3">
              {getPlanBenefits(plan.name).map(
                (feature: string, index: number) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                  >
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-foreground">{feature}</span>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Verification Limit */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                  Monthly Verification Limit
                </h4>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
                  {plan.productMetadata?.check_count || plan.monthlyLimit || 0}{" "}
                  identity verifications per month included
                </p>
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-900 dark:text-green-100">
                  Security & Privacy
                </h4>
                <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                  All your data is protected with the highest security standards
                  and privacy regulations
                </p>
              </div>
            </div>
          </div>

          {/* Downgrade Warning */}
          {isDowngrade && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <span className="text-yellow-600 dark:text-yellow-400 text-xl">
                  ⚠️
                </span>
                <div>
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                    Downgrade Not Allowed
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    This plan is lower than your current plan. You can only
                    upgrade to higher plans. If you need to downgrade, please
                    contact support.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            {!isCurrentPlan && !isDowngrade && (
              <Button
                onClick={handleUpgrade}
                disabled={loading || isDowngrade}
                className={`flex-1 bg-gradient-to-r ${getPlanGradient(
                  plan.name
                )} hover:opacity-90 text-white font-semibold transition-all duration-200`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    {isUpgrade ? "Upgrade Plan" : "Change Plan"}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
            {isDowngrade && (
              <Button
                disabled
                className="flex-1 bg-gray-400 cursor-not-allowed text-white font-semibold"
              >
                Downgrade Not Allowed
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanDetailsDialog;
